import {
    DEFAULT_MODEL,
    MicVAD,
    getDefaultRealTimeVADOptions,
    utils,
  } from "@ricky0123/vad-web";
  import SpeechRecognition, { useSpeechRecognition} from "react-speech-recognition"
  import  { useEffect, useState, useRef, useCallback, useLayoutEffect } from "react";
 
  // Export utils if needed externally
  export { utils };
 
  const defaultReactOptions = {
    startOnLoad: true,
    userSpeakingThreshold: 0.6,
  };
 
  export const getDefaultReactRealTimeVADOptions = (model) => {
    return {
      ...getDefaultRealTimeVADOptions(model),
      ...defaultReactOptions,
    };
  };
 
  const reactOptionKeys = Object.keys(defaultReactOptions);
  const vadOptionKeys = Object.keys(getDefaultRealTimeVADOptions("v5"));
 
  const _filter = (keys, obj) => {
    return keys.reduce((acc, key) => {
      acc[key] = obj[key];
      return acc;
    }, {});
  };
 
  function useOptions(options) {
    const model = options.model ?? DEFAULT_MODEL;
    options = { ...getDefaultReactRealTimeVADOptions(model), ...options };
    const reactOptions = _filter(reactOptionKeys, options);
    const vadOptions = _filter(vadOptionKeys, options);
    return [reactOptions, vadOptions];
  }
 
  function useEventCallback(fn) {
    const ref = useRef(fn);
    useIsomorphicLayoutEffect(() => {
      ref.current = fn;
    });
    return useCallback((...args) => ref.current.apply(void 0, args), []);
  }
 
  export function useMicVAD(options) {
    const [reactOptions, vadOptions] = useOptions(options || {});
    const [userSpeaking, updateUserSpeaking] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errored, setErrored] = useState(false);
    const [stopped, setStopped] = useState(true);
    const [listening, setListening] = useState(false);
    const [vad, setVAD] = useState(null);
    const {transcript, resetTranscript, listening: listening2} = useSpeechRecognition()
 
    const userOnFrameProcessed = useEventCallback(vadOptions.onFrameProcessed);
    vadOptions.onFrameProcessed = useEventCallback((probs, frame) => {
      const isSpeaking = probs.isSpeech > reactOptions.userSpeakingThreshold;
      updateUserSpeaking(isSpeaking);
      if (typeof userOnFrameProcessed === "function") {
        userOnFrameProcessed(probs, frame);
      }
    });
 
    const _onSpeechEnd = useEventCallback(vadOptions.onSpeechEnd);
    const _onSpeechStart = useEventCallback(vadOptions.onSpeechStart);
    const _onSpeechRealStart = useEventCallback(vadOptions.onSpeechRealStart);
    const _onVADMisfire = useEventCallback(vadOptions.onVADMisfire);
 
    vadOptions.onSpeechEnd = _onSpeechEnd;
    vadOptions.onSpeechStart = _onSpeechStart;
    vadOptions.onSpeechRealStart = _onSpeechRealStart;
    vadOptions.onVADMisfire = _onVADMisfire;
 
    useEffect(() => {
      let myvad = null;
      let canceled = false;
 
      const setup = async () => {
        try {
          myvad = await MicVAD.new(vadOptions);
          if (canceled) {
            myvad.destroy();
            return;
          }
          setVAD(myvad);
          setLoading(false);
          if (reactOptions.startOnLoad) {
            myvad.start();
            SpeechRecognition.startListening({continuous:true,language:"en-IN"})
            setListening(true);
          }
        } catch (e) {
          setLoading(false);
          setErrored(e instanceof Error ? e.message : String(e));
        }
      };
      if (!stopped) {
          setup().catch((e) => {
            console.log("VAD setup failed:", e);
          });
      }
 
      return () => {
        myvad?.destroy();
        canceled = true;
        if (!loading && !errored) {
          setListening(false);
        }
      };
    }, [stopped]);
 
    const pause = () => {
      SpeechRecognition.abortListening()
      if (!loading && !errored && vad) {
        vad.pause();
        setListening(false);
        if (vad.stream) {
            vad.stream.getTracks().forEach((t) => t.stop());
        }
        setStopped(true);
      }
    };
 
    const start =  () => {
      if (!loading && !errored && vad) {
        vad.start();
        setListening(true);
      }
    };
    const reStart =  () => {
        setStopped(false)
    };
 
    const toggle = () => {
      listening ? pause() : start();
    };
 
    return {
      listening:listening2,
      errored,
      loading,
      userSpeaking,
      pause,
      start,
      toggle,
      reStart,
      transcript,
      resetTranscript
    };
  }
 
  const useIsomorphicLayoutEffect =
    typeof window !== "undefined" &&
    typeof window.document !== "undefined" &&
    typeof window.document.createElement !== "undefined"
      ? useLayoutEffect
      : useEffect;